import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { User } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth } from "../firebase";
import { db } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";

type AuthContextValue = {
  user: User | null | undefined;
  loading: boolean;
  error: Error | undefined;
  refreshUser: () => void;
  profileVersion: number;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authStateUser, loading, error] = useAuthState(auth);
  const [user, setUser] = useState<User | null | undefined>(authStateUser);
  const [profileVersion, setProfileVersion] = useState(0);

  useEffect(() => {
    setUser(authStateUser);
  }, [authStateUser]);

  // Ensure Firestore users/{uid} exists with email so "existing user" invite check works
  useEffect(() => {
    if (!authStateUser?.uid || !authStateUser.email) return;
    const ref = doc(db, "users", authStateUser.uid);
    setDoc(
      ref,
      {
        email: authStateUser.email.toLowerCase(),
        ...(authStateUser.displayName && {
          displayName: authStateUser.displayName,
        }),
        ...(authStateUser.photoURL && { photoURL: authStateUser.photoURL }),
      },
      { merge: true },
    ).catch(() => {
      // Ignore permission/network errors; user doc may already be maintained elsewhere
    });
  }, [authStateUser?.uid, authStateUser?.email, authStateUser?.displayName, authStateUser?.photoURL]);

  const refreshUser = useCallback(() => {
    setUser(auth.currentUser ?? null);
    setProfileVersion((v) => v + 1);
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    refreshUser,
    profileVersion,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
